import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import usePresenceChannel, {
  PresencePayload,
  PresenceViewer as Viewer,
} from 'app/frontend/base/hooks/usePresenceChannel'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'

type CursorState = {
  viewer: Viewer
  x: number
  y: number
  context: string
}

type Options = {
  planId?: number
  onPlanUpdated?: (plan: RaidPlan) => void
  onPlanDeleted?: (planId: number) => void
}

type RaidPlanChannelPayload = PresencePayload & {
  viewer?: Viewer
  x?: number | null
  y?: number | null
  context?: string
  raid_plan?: RaidPlan
  plan_id?: number
  client_cursor_id?: string
}

const useRaidPlanChannel = ({
  planId,
  onPlanUpdated,
  onPlanDeleted,
}: Options) => {
  const [cursors, setCursors] = useState<Record<string, CursorState>>({})
  const onPlanUpdatedRef = useRef(onPlanUpdated)
  const onPlanDeletedRef = useRef(onPlanDeleted)
  const localCursorIdRef = useRef(
    `cursor-${Math.random().toString(36).slice(2)}`,
  )

  useEffect(() => {
    onPlanUpdatedRef.current = onPlanUpdated
  }, [onPlanUpdated])

  useEffect(() => {
    onPlanDeletedRef.current = onPlanDeleted
  }, [onPlanDeleted])

  const handlePayload = useCallback((payload: RaidPlanChannelPayload) => {
    if (payload.type === 'presence') {
      const nextViewers = payload.viewers ?? []
      setCursors((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([key]) =>
            nextViewers.some((viewer) => viewer.key === key),
          ),
        ),
      )
    }

    if (payload.type === 'cursor_moved') {
      if (payload.client_cursor_id === localCursorIdRef.current) return
      const viewer = payload.viewer
      if (!viewer) return
      const context = payload.context
      if (!context) return
      if (payload.x === null || payload.y === null) {
        setCursors((prev) => {
          const next = { ...prev }
          delete next[viewer.key]
          return next
        })
        return
      }
      if (payload.x === undefined || payload.y === undefined) return
      const x = payload.x
      const y = payload.y
      setCursors((prev) => ({
        ...prev,
        [viewer.key]: {
          viewer,
          x,
          y,
          context,
        },
      }))
    }

    if (payload.type === 'plan_updated') {
      if (!payload.raid_plan) return
      onPlanUpdatedRef.current?.(payload.raid_plan)
    }

    if (payload.type === 'plan_deleted') {
      if (payload.plan_id === undefined) return
      onPlanDeletedRef.current?.(payload.plan_id)
    }
  }, [])

  const { viewers, requiresReload, perform } =
    usePresenceChannel<RaidPlanChannelPayload>({
      channelName: 'RaidPlanChannel',
      channelId: planId,
      channelIdParam: 'plan_id',
      onPayload: handlePayload,
    })

  useEffect(() => {
    if (!planId) {
      setCursors({})
    }
  }, [planId])

  const sendCursor = useCallback(
    (x: number | null, y: number | null, context: string) => {
      if (viewers.length < 2) return
      perform('cursor', {
        x,
        y,
        context,
        client_cursor_id: localCursorIdRef.current,
      })
    },
    [perform, viewers.length],
  )

  const cursorList = useMemo(() => Object.values(cursors), [cursors])

  return {
    viewers,
    cursors: cursorList,
    requiresReload,
    sendCursor,
  }
}

export default useRaidPlanChannel
